import { getJstFullYear, getJstMonth } from "@/utils/jst_date"

export type YearMonthValue = { year: number; month: number }

/**
 * 年月ドロップダウンのオプションを生成する。
 * 来月 → 今月 → 先月 → ... → 12ヶ月前 の順で返す（計14ヶ月）。
 */
export const generateYearMonthOptions = (today: Date): YearMonthValue[] => {
	const options: YearMonthValue[] = []
	const baseYear = getJstFullYear(today)
	const baseMonth = getJstMonth(today) + 1 // 1-indexed

	// 来月
	const nextMonth =
		baseMonth === 12
			? { year: baseYear + 1, month: 1 }
			: { year: baseYear, month: baseMonth + 1 }
	options.push(nextMonth)

	// 今月 → 12ヶ月前（計13ヶ月）
	for (let i = 0; i <= 12; i++) {
		let y = baseYear
		let m = baseMonth - i
		while (m <= 0) {
			y--
			m += 12
		}
		options.push({ year: y, month: m })
	}

	return options
}

/**
 * YearMonthValue を "YYYY-MM" 形式の文字列に変換する。
 */
export const formatYearMonth = (ym: YearMonthValue): string =>
	`${ym.year}-${String(ym.month).padStart(2, "0")}`

/**
 * YearMonthValue を "YYYY年M月" 形式の日本語表示用文字列に変換する。
 */
export const formatYearMonthLabel = (ym: YearMonthValue): string =>
	`${ym.year}年${ym.month}月`

/**
 * 今月の YearMonthValue を返す。
 */
export const getCurrentYearMonth = (today: Date): YearMonthValue => ({
	year: getJstFullYear(today),
	month: getJstMonth(today) + 1,
})

/**
 * 先月の YearMonthValue を返す（1月の場合は前年12月に繰り下げる）。
 */
export const getLastYearMonth = (today: Date): YearMonthValue => {
	const current = getCurrentYearMonth(today)
	if (current.month === 1) {
		return { year: current.year - 1, month: 12 }
	}
	return { year: current.year, month: current.month - 1 }
}

/**
 * 2 つの YearMonthValue が同じ年月かを判定する。
 */
export const isSameYearMonth = (
	a: YearMonthValue,
	b: YearMonthValue
): boolean => a.year === b.year && a.month === b.month
